import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { applyPhoneNumberMask } from "@plasma/utils/phone-numbers";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { employeesGetById, locationsGetAllMine } from "~/api-refs";
import { Breadcrumbs } from "~/components/ui/breadcrumbs";
import { Button } from "~/components/ui/button";

import { PinDisplay } from "./employee-list-table-columns";

// V4.1: Simplified component - all field editing moved to route-based modals
export const EmployeeProfileBlocks = ({
  employeeId,
}: {
  employeeId: Id<"employees">;
}) => {
  const navigate = useNavigate();

  const { data: employee, isLoading: isLoadingEmployee } = useQuery(
    convexQuery(employeesGetById, { id: employeeId })
  );
  const { data: allMerchantLocations = [] } = useQuery(
    convexQuery(locationsGetAllMine, {})
  );

  if (isLoadingEmployee) {
    return (
      <div className="flex h-dvh items-center justify-center">Loading...</div>
    );
  }

  if (!employee) {
    return (
      <div className="flex h-dvh items-center justify-center">
        Employee not found
      </div>
    );
  }

  // V4.1: Navigate to route-based toggle status modal
  const handleToggleStatusClick = () => {
    void navigate({ to: `/employees/${employeeId}/toggle-status` });
  };

  // V4.1: Navigate to route-based field edit modal
  const navigateToEditField = (field: string) => {
    void navigate({ to: `/employees/${employeeId}/edit/${field}` });
  };

  const employeeLocations = employee.locationAssignments
    .map((assignment) => assignment.location)
    .filter(
      (location): location is NonNullable<typeof location> => location !== null
    );
  const employeeRoles = employee.employeeRoles
    .map((r) => r.merchantRole)
    .filter((r): r is NonNullable<typeof r> => r !== null);

  return (
    <div className="flex gap-6">
      <div className="w-150 space-y-6">
        <Breadcrumbs
          crumbs={[
            { to: "/employees", label: "Employees" },
            {
              to: `/employees/${employeeId}`,
              label: `${employee.firstName} ${employee.lastName}`,
            },
          ]}
        />
        <div className="bg-muted flex items-center gap-5 rounded-lg px-5 py-4">
          <img
            alt={`${employee.firstName}'s avatar`}
            className="size-16 rounded-full"
            height={64}
            src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
            width={64}
          />
          <h1 className="h2 font-heading text-foreground text-balance">
            {employee.firstName} {employee.lastName}
          </h1>
        </div>
        <div>
          <div className="space-y-1 py-5">
            <h2 className="subtitle-sm text-foreground">Profile</h2>
            <p className="body-sm text-foreground">
              View and update personal details for your employee.
            </p>
          </div>
        </div>
        {/* V4.1: Profile fields - click navigates to route-based edit modal */}
        <div>
          <EmployeeProfileRow
            id="employeeProfilePhoto"
            label="Photo"
            onClick={() => navigateToEditField("photo")}
          >
            <img
              alt={`${employee.firstName}'s avatar`}
              className="size-9 rounded-full"
              height={36}
              src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
              width={36}
            />
          </EmployeeProfileRow>
          <EmployeeProfileRow
            id="employeeProfileName"
            label="Name"
            onClick={() => navigateToEditField("name")}
          >
            {employee.firstName} {employee.lastName}
          </EmployeeProfileRow>
          <EmployeeProfileRow
            id="employeeProfilePhoneNumber"
            label="Phone Number"
            onClick={() => navigateToEditField("phone")}
          >
            {employee?.phoneNumber
              ? applyPhoneNumberMask(employee.phoneNumber)
              : ""}
          </EmployeeProfileRow>
          <EmployeeProfileRow
            id="employeeProfileEmail"
            label="Email address"
            onClick={() => navigateToEditField("email")}
          >
            {employee.email}
          </EmployeeProfileRow>
          <EmployeeProfileRow
            id="employeeProfileHourlyRate"
            label="Hourly Rate"
            onClick={() => navigateToEditField("hourlyRate")}
          >
            ${employee.hourlyRate}
          </EmployeeProfileRow>
        </div>
        <div className="space-y-1 py-5">
          <h2 className="subtitle-sm text-foreground">
            Assigned Roles & Locations
          </h2>
          <p className="body-sm text-foreground">
            Manage and organize your employee.
          </p>
        </div>
        <div>
          <EmployeeProfileRow
            id="employeeProfilePIN"
            label="PIN"
            onClick={() => navigateToEditField("pin")}
          >
            <PinDisplay pin={employee.pin} />
          </EmployeeProfileRow>
          <EmployeeProfileRow
            id="employeeProfileRoles"
            label="Roles"
            onClick={() => navigateToEditField("roles")}
          >
            {employeeRoles.map((role, i) => (
              <span key={role._id}>
                {role.name}
                {i !== employeeRoles.length - 1 ? ", " : ""}
              </span>
            ))}
          </EmployeeProfileRow>
          <EmployeeProfileRow
            id="employeeProfileLocations"
            label="Locations"
            onClick={() => navigateToEditField("locations")}
          >
            {employeeLocations.length === allMerchantLocations.length ? (
              <p className="body-sm text-foreground">
                All and future locations
              </p>
            ) : (
              <div className="flex flex-col gap-5">
                {employeeLocations.map((location) => (
                  <div
                    className="flex flex-1 flex-row gap-2"
                    key={location._id}
                  >
                    <img
                      alt={`${location.name} logo`}
                      className="size-5 rounded-full"
                      height={20}
                      src={
                        location.logoUrl ??
                        "/images/location-logo-placeholder.svg"
                      }
                      width={20}
                    />
                    <span>{location.name}</span>
                  </div>
                ))}
              </div>
            )}
          </EmployeeProfileRow>
        </div>
        <div className="space-y-1 py-5">
          <h2 className="subtitle-sm text-foreground">Permissions</h2>
          <p className="body-sm text-foreground">
            Customize employee access in Plasma POS.
          </p>
        </div>
        <div className="space-y-1 py-5">
          <h2 className="subtitle-sm text-foreground">Danger Zone</h2>
          <p className="body-sm text-foreground">
            {employee.disabled ? "Enable" : "Disable"} employee from Plasma POS.
          </p>
        </div>
        {/* V4.1: Navigate to route-based toggle status modal */}
        <div className="flex space-x-4">
          <Button onClick={handleToggleStatusClick} variant="destructive">
            {employee.disabled ? "Enable" : "Disable"} Employee
          </Button>
        </div>
      </div>
      <div className="w-100">{/* Graphs here */}</div>
      {/* V4.1: Outlet for child routes (edit field, toggle-status modals) */}
      <Outlet />
    </div>
  );
};

// V4.1: Simplified row component - just a clickable button that navigates to edit route
const EmployeeProfileRow = ({
  id,
  label,
  className,
  children,
  onClick,
}: {
  id: string;
  label: string;
  className?: string;
  children: React.ReactNode;
  onClick: () => void;
}) => {
  return (
    <button
      className={twMerge(
        "body-sm border-border text-foreground hover:bg-muted/50 flex w-full cursor-pointer gap-2 border-t py-5 transition-colors",
        className
      )}
      id={id}
      onClick={onClick}
      type="button"
    >
      <p className="label text-foreground w-40">{label}</p>
      <div className="body-sm text-foreground leading-tight">{children}</div>
      <div className="flex-1" />
      <div className="flex justify-start">
        <ChevronRight className="size-5" />
      </div>
    </button>
  );
};
